import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { CaseStudyForm } from "@/components/dashboard/profile/portfolio/case-study/CaseStudyForm";
import { useToast } from "@/hooks/use-toast";
import { useCaseStudyById } from "@/hooks/useCaseStudy";
import { CaseStudyItem, CaseStudyVersion } from "@/types/case-study";
import CustomLayout from "@/components/layout/CustomLayout";
import { useCaseStudyFormState } from "@/hooks/domains/case-study/useCaseStudyFormState";
import { useCaseStudyAutosave } from "@/hooks/domains/case-study/useCaseStudyAutosave";
import { useCaseStudyHistory, restoreFromSnapshot } from "@/hooks/domains/case-study/useCaseStudyHistory";
import { useVersionHistory } from "@/hooks/domains/case-study/useVersionHistory";
import { CaseStudyService } from "@/services/supabase/caseStudies";
import { SaveVersionDialog } from "@/components/dashboard/profile/portfolio/SaveVersionDialog";
import { RestoreVersionDialog } from "@/components/dashboard/profile/portfolio/RestoreVersionDialog";
import { formatDistanceToNow } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const ACTIVE_SECTION_STORAGE_PREFIX = "caseStudyActiveSection";
const getActiveSectionStorageKey = (caseStudyId?: string) =>
  caseStudyId ? `${ACTIVE_SECTION_STORAGE_PREFIX}:${caseStudyId}` : null;

export default function CaseStudyEditorPageV2() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isSaving, setIsSaving] = useState(false);
  const [saveTrigger, setSaveTrigger] = useState<{ asDraft: boolean } | null>(null);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [showSaveVersionDialog, setShowSaveVersionDialog] = useState(false);
  const [showRestoreVersionDialog, setShowRestoreVersionDialog] = useState(false);
  const [versionToRestore, setVersionToRestore] = useState<CaseStudyVersion | null>(null);
  const [areAllCollapsed, setAreAllCollapsed] = useState(false);
  const [isRefreshingSession, setIsRefreshingSession] = useState(false);
  const [sessionRefreshAttempted, setSessionRefreshAttempted] = useState(false);
  const addSectionHandlerRef = React.useRef<((sectionType: string) => void) | null>(null);
  const addContentBlockHandlerRef = React.useRef<((blockType: string) => void) | null>(null);

  // Check if we have imported data from navigation state
  const importedData = location.state?.importedData as Partial<CaseStudyItem> | undefined;

  // Use the real Supabase hook instead of mock data (only if editing existing)
  const { data: initialData, isLoading, error } = useCaseStudyById(id || '');
  // Prioritize imported data over fetched data
  const dataToUse = importedData || initialData;

  // Session refresh on error - attempt to refresh session if we get "not found" error
  useEffect(() => {
    const attemptSessionRefresh = async () => {
      // Only attempt refresh once per error, and only if we have an error
      if (!error || sessionRefreshAttempted || isRefreshingSession) return;

      // Check if error is likely due to session expiration
      const errorMessage = error?.message?.toLowerCase() || '';
      const isSessionError = errorMessage.includes('not found') ||
                           errorMessage.includes('unauthorized') ||
                           errorMessage.includes('forbidden');

      if (!isSessionError) return;

      console.log('[CaseStudyEditor] Detected potential session error, attempting refresh...');
      setIsRefreshingSession(true);
      setSessionRefreshAttempted(true);

      try {
        // Attempt to refresh the session
        const { data, error: refreshError } = await supabase.auth.refreshSession();

        if (refreshError) {
          console.error('[CaseStudyEditor] Session refresh failed:', refreshError);
          // Redirect to auth with return URL
          toast({
            title: "Session Expired",
            description: "Your session has expired. Please sign in again.",
            variant: "destructive",
          });
          setTimeout(() => {
            navigate(`/auth?returnTo=${encodeURIComponent(location.pathname)}`);
          }, 2000);
          return;
        }

        if (data.session) {
          console.log('[CaseStudyEditor] Session refreshed successfully');
          // Show success message
          toast({
            title: "Session Refreshed",
            description: "Reloading case study...",
          });

          // Invalidate and refetch the case study
          await queryClient.invalidateQueries({ queryKey: ['case-study', id] });

          // Reset the refresh state after a short delay
          setTimeout(() => {
            setIsRefreshingSession(false);
            setSessionRefreshAttempted(false);
          }, 1000);
        }
      } catch (err) {
        console.error('[CaseStudyEditor] Error during session refresh:', err);
        setIsRefreshingSession(false);
      }
    };

    attemptSessionRefresh();
  }, [error, sessionRefreshAttempted, isRefreshingSession, id, queryClient, navigate, location.pathname, toast]);

  // Session expiry warning - show toast 2 minutes before expiry
  useEffect(() => {
    const checkSessionExpiry = async () => {
      const { data: { session } } = await supabase.auth.getSession();

      if (!session?.expires_at) return;

      const expiresAt = session.expires_at * 1000; // Convert to milliseconds
      const now = Date.now();
      const timeUntilExpiry = expiresAt - now;
      const twoMinutes = 2 * 60 * 1000;

      // If session expires in less than 2 minutes, show warning
      if (timeUntilExpiry > 0 && timeUntilExpiry <= twoMinutes) {
        const minutesLeft = Math.ceil(timeUntilExpiry / 60000);
        toast({
          title: "Session Expiring Soon",
          description: `Your session will expire in ${minutesLeft} minute${minutesLeft !== 1 ? 's' : ''}. Your work is being saved automatically.`,
          duration: 10000,
        });
      }
    };

    // Check immediately
    checkSessionExpiry();

    // Check every minute
    const intervalId = setInterval(checkSessionExpiry, 60000);

    return () => clearInterval(intervalId);
  }, [toast]);

  // Form state management
  const {
    caseStudy,
    setCaseStudy: originalSetCaseStudy,
    activeSection,
    setActiveSection,
    showSectionSelector,
    setShowSectionSelector,
    isDetailsCollapsed,
    setIsDetailsCollapsed,
    titleError,
    setTitleError,
    isTitleValid,
    validateTitle,
  } = useCaseStudyFormState({ initialData: dataToUse as CaseStudyItem | Partial<CaseStudyItem> });

  // Persist the last active section so block editors stay editable after visiting preview
  useEffect(() => {
    if (typeof window === "undefined") return;
    const storageKey = getActiveSectionStorageKey(caseStudy?.id);
    if (!storageKey) return;

    if (activeSection) {
      window.sessionStorage.setItem(storageKey, activeSection);
    } else {
      window.sessionStorage.removeItem(storageKey);
    }
  }, [caseStudy?.id, activeSection]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (activeSection) return;
    if (!caseStudy?.id || !caseStudy.sections?.length) return;

    const storageKey = getActiveSectionStorageKey(caseStudy.id);
    if (!storageKey) return;

    const storedSectionId = window.sessionStorage.getItem(storageKey);
    if (storedSectionId && caseStudy.sections.some(section => section.id === storedSectionId)) {
      setActiveSection(storedSectionId);
    } else if (storedSectionId) {
      window.sessionStorage.removeItem(storageKey);
    }
  }, [caseStudy?.id, caseStudy.sections, activeSection, setActiveSection]);

  // History management for undo/redo
  const {
    undo: performUndo,
    redo: performRedo,
    canUndo,
    canRedo,
    wrapSetState,
    clearHistory,
  } = useCaseStudyHistory(caseStudy, { enabled: true, debounceMs: 500 });

  // Wrap setCaseStudy to track history
  const setCaseStudy = wrapSetState(originalSetCaseStudy);

  // Version history management
  const {
    versions,
    isLoading: isLoadingVersions,
    isSaving: isSavingVersion,
    loadVersions,
    createVersion,
    getVersion,
  } = useVersionHistory({ caseStudyId: id });

  // Load versions when case study ID is available
  useEffect(() => {
    if (id) {
      loadVersions();
    }
  }, [id, loadVersions]);

  // Undo handler - restores from history snapshot
  const handleUndo = () => {
    if (!canUndo || isSaving) return;
    const snapshot = performUndo();
    if (snapshot) {
      const restoredCaseStudy = restoreFromSnapshot(caseStudy, snapshot);
      originalSetCaseStudy(restoredCaseStudy); // Use original to skip history tracking
    }
  };

  // Redo handler - restores from history snapshot
  const handleRedo = () => {
    if (!canRedo || isSaving) return;
    const snapshot = performRedo();
    if (snapshot) {
      const restoredCaseStudy = restoreFromSnapshot(caseStudy, snapshot);
      originalSetCaseStudy(restoredCaseStudy); // Use original to skip history tracking
    }
  };

  // Autosave hook for ALL case studies (imported and from scratch)
  const { saveStatus, currentCaseStudyId, isSaving: isAutosaving } = useCaseStudyAutosave({
    caseStudy,
    caseStudyId: id, // Use URL ID (hook will track its own ID internally)
    debounceMs: 2000,
    enabled: true, // Always enabled - autosave hook handles both imported and from-scratch
    isManualSaving: isSaving, // Disable autosave when manual save is in progress
    onSaveSuccess: (savedCaseStudy) => {
      // Don't overwrite user's current input - only update metadata fields
      // This prevents race condition where saved data overwrites newer user input
      setCaseStudy(prev => ({
        ...prev,
        id: savedCaseStudy.id,
        slug: savedCaseStudy.slug,
        created_at: savedCaseStudy.created_at,
        updated_at: savedCaseStudy.updated_at,
        profile_id: savedCaseStudy.profile_id,
        // Keep user's current form data for all editable fields
      }));
    },
    onSaveError: (error) => {
      console.error('[CaseStudyEditorPageV2] Autosave failed:', error);
      toast.error('Failed to auto-save your changes. Please try again.');
    },
  });

  // Update URL when case study is first created (without page reload)
  useEffect(() => {
    if (currentCaseStudyId && !id) {
      // Replace URL without page reload
      window.history.replaceState(null, '', `/dashboard/portfolio/case-study/${currentCaseStudyId}`);
    }
  }, [currentCaseStudyId, id]);

  // Keyboard shortcuts for undo/redo
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't intercept if user is typing in an input or textarea
      const target = e.target as HTMLElement;
      const isEditable =
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable ||
        target.closest('[contenteditable="true"]');

      if (isEditable || isSaving) return;

      // Detect platform (Mac uses metaKey, Windows/Linux uses ctrlKey)
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const modKey = isMac ? e.metaKey : e.ctrlKey;

      // Undo: Cmd+Z (Mac) or Ctrl+Z (Windows/Linux)
      if (modKey && e.key === 'z' && !e.shiftKey) {
        if (canUndo) {
          e.preventDefault();
          handleUndo();
        }
      }

      // Redo: Cmd+Shift+Z (Mac) or Ctrl+Shift+Z / Ctrl+Y (Windows/Linux)
      if (
        (modKey && e.shiftKey && e.key === 'z') ||
        (modKey && e.key === 'y' && !isMac)
      ) {
        if (canRedo) {
          e.preventDefault();
          handleRedo();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [canUndo, canRedo, handleUndo, handleRedo, isSaving]);

  const handleSave = async (savedCaseStudy: CaseStudyItem) => {
    // Update the local case study state with the saved case study
    setCaseStudy(savedCaseStudy);

    // Auto-create version when publishing
    if (savedCaseStudy.status === 'published' && savedCaseStudy.id) {
      try {
        await createVersion(savedCaseStudy, undefined, 'auto_publish');
      } catch (error) {
        console.error('[CaseStudyEditorPageV2] Failed to create auto-version:', error);
        // Don't block the save flow if version creation fails
      }
    }

    if (isPreviewMode) {
      // If we were in preview mode, navigate to preview instead of portfolio
      setIsPreviewMode(false); // Reset preview mode flag
      navigate(`/case-study/preview/${savedCaseStudy.id}`);
    } else {
      // Stay on editor after save; ensure URL reflects the saved case study ID
      if (!id || id !== savedCaseStudy.id) {
        navigate(`/dashboard/portfolio/case-study/${savedCaseStudy.id}`, { replace: true });
      } 
    }
  };

  const handleHeaderSave = (asDraft: boolean) => {
    setSaveTrigger({ asDraft });
  };

  const handlePreview = async () => {
    
    if (caseStudy.id) {
      // Case study already exists, navigate to preview
      navigate(`/case-study/preview/${caseStudy.id}`);
    } else {
      // Case study doesn't exist yet, save as draft first then preview
      setIsPreviewMode(true); // Set preview mode flag
      setSaveTrigger({ asDraft: true });
    }
  };

  const handleCancel = () => {
    navigate("/dashboard/portfolio");
  };

  const handleToggleCollapseAll = () => {
    const newCollapsedState = !areAllCollapsed;
    setAreAllCollapsed(newCollapsedState);

    // Collapse/expand the details card
    setIsDetailsCollapsed(newCollapsedState);

    // Collapse/expand all sections
    originalSetCaseStudy((prev) => ({
      ...prev,
      sections: prev.sections?.map((section) => ({
        ...section,
        isCollapsed: newCollapsedState,
      })) || [],
    }));
  };

  const handleDeleteClick = () => {
    setShowDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    if (!caseStudy.id) {
      toast({
        title: "Error",
        description: "Cannot delete a case study that hasn't been saved yet.",
        variant: "destructive",
      });
      return;
    }

    setIsDeleting(true);
    try {
      await CaseStudyService.deleteCaseStudy(caseStudy.id);
      toast({
        title: "Success",
        description: "Case study deleted successfully.",
      });
      navigate("/dashboard/portfolio");
    } catch (error) {
      console.error("Error deleting case study:", error);
      toast({
        title: "Error",
        description: "Failed to delete case study. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  const handleDuplicate = async () => {
    if (!caseStudy.id) {
      toast({
        title: "Error",
        description: "Cannot duplicate a case study that hasn't been saved yet.",
        variant: "destructive",
      });
      return;
    }

    setIsDuplicating(true);
    try {
      const duplicatedCaseStudy = await CaseStudyService.duplicateCaseStudy(caseStudy.id);
      toast({
        title: "Success",
        description: "Case study duplicated successfully.",
      });
      // Navigate to the duplicated case study editor
      navigate(`/dashboard/portfolio/case-study/${duplicatedCaseStudy.id}`);
    } catch (error) {
      console.error("Error duplicating case study:", error);
      toast({
        title: "Error",
        description: "Failed to duplicate case study. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsDuplicating(false);
    }
  };

  const handleSaveVersion = () => {
    if (!caseStudy.id) {
      toast({
        title: "Error",
        description: "Cannot save version of a case study that hasn't been saved yet.",
        variant: "destructive",
      });
      return;
    }
    setShowSaveVersionDialog(true);
  };

  const handleSaveVersionConfirm = async (label: string) => {
    if (!caseStudy.id) return;

    try {
      await createVersion(caseStudy, label || undefined, 'manual');
      toast({
        title: "Success",
        description: "Version saved successfully.",
      });
    } catch (error) {
      console.error("Error saving version:", error);
      toast({
        title: "Error",
        description: "Failed to save version. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleRestoreVersionClick = (version: CaseStudyVersion) => {
    setVersionToRestore(version);
    setShowRestoreVersionDialog(true);
  };

  const handleRestoreVersionConfirm = async (version: CaseStudyVersion) => {
    try {
      // Restore the case study from the version snapshot
      const restoredCaseStudy = restoreFromSnapshot(caseStudy, version.snapshot);

      // Clear undo/redo history since we're restoring
      clearHistory();

      // Update the case study state (bypasses history tracking)
      originalSetCaseStudy(restoredCaseStudy);

      toast({
        title: "Success",
        description: `Restored to ${version.label || `Version ${version.version_number}`}`,
      });
    } catch (error) {
      console.error("Error restoring version:", error);
      toast({
        title: "Error",
        description: "Failed to restore version. Please try again.",
        variant: "destructive",
      });
    }
  };

  const versionHistoryItems = useMemo(() => {
    return versions.map((version) => ({
      id: version.id,
      title: version.label || `Version ${version.version_number}`,
      timestamp: formatDistanceToNow(new Date(version.created_at), { addSuffix: true }),
      onRestore: () => handleRestoreVersionClick(version),
    }));
  }, [versions]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header skeleton */}
          <div className="animate-pulse space-y-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="h-8 w-16 bg-gray-200 rounded"></div>
                <div className="h-8 w-48 bg-gray-200 rounded"></div>
              </div>
              <div className="flex gap-2">
                <div className="h-8 w-24 bg-gray-200 rounded"></div>
                <div className="h-8 w-20 bg-gray-200 rounded"></div>
                <div className="h-8 w-20 bg-gray-200 rounded"></div>
              </div>
            </div>

            {/* Form skeleton */}
            <div className="space-y-6">
              <div className="h-32 bg-gray-200 rounded-lg"></div>
              <div className="space-y-4">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="h-48 bg-gray-200 rounded-lg"></div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    // Show refreshing state if we're attempting to refresh the session
    if (isRefreshingSession) {
      return (
        <div className="min-h-screen bg-white flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Refreshing Session...</h2>
            <p className="text-gray-600">
              Your session expired. Attempting to refresh and reload your case study.
            </p>
          </div>
        </div>
      );
    }

    // Show error only if session refresh was attempted and failed
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-lg font-semibold text-red-600 mb-2">Error Loading Case Study</h2>
          <p className="text-gray-600 mb-4">
            {error.message === "Case study not found"
              ? "This case study could not be found. Your session may have expired."
              : error.message || "There was an error loading the case study."}
          </p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-gray-200 text-gray-900 rounded hover:bg-gray-300"
            >
              Retry
            </button>
            <button
              onClick={() => navigate("/dashboard/portfolio")}
              className="px-4 py-2 bg-gray-900 text-white rounded hover:bg-gray-800"
            >
              Back to Portfolio
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-transparent animate-fade-in flex flex-col">
      {/* CustomLayout with case study header */}
      <CustomLayout
        caseStudyMode={true}
        caseStudy={caseStudy}
        isSaving={isSaving}
        isTitleValid={isTitleValid}
        saveStatus={saveStatus}
        showAutosaveIndicator={true}
        onCancel={handleCancel}
        onSave={handleHeaderSave}
        onPreview={handlePreview}
        onAddSection={(sectionType) => addSectionHandlerRef.current?.(sectionType)}
        onAddContentBlock={(blockType) => addContentBlockHandlerRef.current?.(blockType)}
        onUndo={handleUndo}
        onRedo={handleRedo}
        canUndo={canUndo}
        canRedo={canRedo}
        onSaveVersion={handleSaveVersion}
        onShowVersionHistory={() => {
        }}
        onDuplicate={handleDuplicate}
        onDelete={handleDeleteClick}
        versionHistoryItems={versionHistoryItems}
      >
        <div className="bg-[#EBECF0] flex flex-col items-stretch pt-24 pb-10 min-h-screen">
          <div className="max-w-7xl mx-auto w-full">
            <CaseStudyForm
              initialData={dataToUse as CaseStudyItem}
              onSave={handleSave}
              onCancel={handleCancel}
              onPreview={handlePreview}
              // Pass form state and callbacks
              caseStudy={caseStudy}
              setCaseStudy={setCaseStudy}
              activeSection={activeSection}
              setActiveSection={setActiveSection}
              showSectionSelector={showSectionSelector}
              setShowSectionSelector={setShowSectionSelector}
              onAddSectionFromHeader={(handler) => { addSectionHandlerRef.current = handler; }}
              onAddContentBlockFromHeader={(handler) => { addContentBlockHandlerRef.current = handler; }}
              isDetailsCollapsed={isDetailsCollapsed}
              setIsDetailsCollapsed={setIsDetailsCollapsed}
              onToggleCollapseAll={handleToggleCollapseAll}
              areAllCollapsed={areAllCollapsed}
              titleError={titleError}
              setTitleError={setTitleError}
              isTitleValid={isTitleValid}
              validateTitle={validateTitle}
              saveTrigger={saveTrigger}
              onSaveTriggered={() => setSaveTrigger(null)}
            />
          </div>
        </div>
      </CustomLayout>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Case Study?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "<strong>{caseStudy.title || 'Untitled Case Study'}</strong>"?
              This action cannot be undone and will permanently remove the case study and all its content.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Save Version Dialog */}
      <SaveVersionDialog
        open={showSaveVersionDialog}
        onOpenChange={setShowSaveVersionDialog}
        onSave={handleSaveVersionConfirm}
        isSaving={isSavingVersion}
      />

      {/* Restore Version Dialog */}
      <RestoreVersionDialog
        open={showRestoreVersionDialog}
        onOpenChange={setShowRestoreVersionDialog}
        version={versionToRestore}
        onRestore={handleRestoreVersionConfirm}
        isRestoring={false}
      />
    </div>
  );
}
